// src/components/Dashboard/AIInsights.jsx
import {
  ArrowForward,
  Close as CloseIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';

// 1) Utility function to shorten text
const getShortText = (text, length = 80) => {
  if (!text) return '';
  return text.length > length ? `${text.substring(0, length)}...` : text;
};

// 2) Recommendation Card
const InsightCard = ({ insight, onViewDetails }) => {
  const shortDescription = getShortText(insight.description, 80);
  const shortRecommendation = getShortText(insight.recommendation, 80);

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: (theme) => theme.shadows[8],
        },
        transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
      }}
    >
      <CardHeader
        sx={{
          p: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
        title={
          <Box display="flex" justifyContent="space-between" alignItems="flex-start">
            <Typography variant="h6" sx={{ fontSize: '1.1rem', flexGrow: 1, mr: 2 }}>
              {insight.title}
            </Typography>
            <Chip
              label={insight.priority}
              color={insight.priority === 'HIGH' ? 'error' : 'warning'}
              size="small"
            />
          </Box>
        }
      />
      <CardContent sx={{ p: 2, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Description Header + Truncated Description */}
        {shortDescription && (
          <>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
              Description
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 1 }}>
              {shortDescription}{' '}
              {insight.description && insight.description.length > 80 && (
                <Chip
                  label="more"
                  size="small"
                  onClick={() => onViewDetails(insight)}
                  sx={{ ml: 1, cursor: 'pointer' }}
                />
              )}
            </Typography>
          </>
        )}

        {/* Recommendation Header + Truncated Recommendation */}
        {shortRecommendation && (
          <>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
              Recommendation
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              {shortRecommendation}{' '}
              {insight.recommendation && insight.recommendation.length > 80 && (
                <Chip
                  label="more"
                  size="small"
                  onClick={() => onViewDetails(insight)}
                  sx={{ ml: 1, cursor: 'pointer' }}
                />
              )}
            </Typography>
          </>
        )}

        {/* Impact */}
        {insight.impact && (
          <Typography color="primary" sx={{ mb: 2 }}>
            {insight.impact}
          </Typography>
        )}

        {/* View Details Button */}
        <Box sx={{ mt: 'auto' }}>
          <Button
            variant="outlined"
            onClick={() => onViewDetails(insight)}
            endIcon={<ArrowForward />}
            fullWidth
          >
            View Details
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

// 3) Emerging Patterns Card (unchanged from your original)
const PatternCard = ({ pattern, onViewDetails }) => (
  <Card
    onClick={() => onViewDetails(pattern)}
    sx={{
      cursor: 'pointer',
      height: '100%',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: (theme) => theme.shadows[8],
      },
      transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
    }}
  >
    <CardHeader
      sx={{
        p: 2,
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
      title={
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
            {pattern.pattern}
          </Typography>
          <Chip
            label={`${(pattern.confidence * 100).toFixed(0)}%`}
            color="primary"
            size="small"
          />
        </Box>
      }
    />
    <CardContent sx={{ p: 2 }}>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        {pattern.description}
      </Typography>
      {pattern.growth_rate && (
        <Typography color="error.main" variant="body2">
          {pattern.growth_rate}
        </Typography>
      )}
    </CardContent>
  </Card>
);

// 4) Action Priorities Card (unchanged from your original)
const ActionPriorityCard = ({ action, onViewDetails }) => (
  <Card
    sx={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: (theme) => theme.shadows[8],
      },
      transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
    }}
  >
    <CardHeader
      sx={{
        p: 2,
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
      title={
        <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
          {action.initiative}
        </Typography>
      }
    />
    <CardContent sx={{ p: 2, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6}>
          <Typography variant="body2" color="text.secondary">
            Timeline
          </Typography>
          <Typography variant="body1">{action.timeline}</Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="body2" color="text.secondary">
            Impact
          </Typography>
          <Typography variant="body1">{action.impact_potential}</Typography>
        </Grid>
      </Grid>

      <Box sx={{ mt: 'auto' }}>
        <Button
          variant="outlined"
          onClick={() => onViewDetails(action)}
          endIcon={<ArrowForward />}
          fullWidth
        >
          View Details
        </Button>
      </Box>
    </CardContent>
  </Card>
);

// 5) Main Component
const AIInsights = ({ insights }) => {
  const [selectedInsight, setSelectedInsight] = useState(null);

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
        AI Insights
      </Typography>

      <Grid container spacing={3}>
        {/* Recommendations */}
        <Grid item xs={12}>
          <Typography variant="h5" gutterBottom color="primary" sx={{ mb: 2 }}>
            Recommendations
          </Typography>
          <Grid container spacing={2}>
            {insights.recommendations.map((rec, idx) => (
              <Grid item xs={12} md={4} key={idx}>
                <InsightCard insight={rec} onViewDetails={setSelectedInsight} />
              </Grid>
            ))}
          </Grid>
        </Grid>

        {/* Emerging Patterns */}
        <Grid item xs={12}>
          <Typography variant="h5" gutterBottom color="primary" sx={{ mb: 2 }}>
            Emerging Patterns
          </Typography>
          <Grid container spacing={2}>
            {insights.emerging_patterns.map((pattern, idx) => (
              <Grid item xs={12} md={4} key={idx}>
                <PatternCard pattern={pattern} onViewDetails={setSelectedInsight} />
              </Grid>
            ))}
          </Grid>
        </Grid>

        {/* Action Priorities */}
        <Grid item xs={12}>
          <Typography variant="h5" gutterBottom color="primary" sx={{ mb: 2 }}>
            Action Priorities
          </Typography>
          <Grid container spacing={2}>
            {insights.action_priorities.map((action, idx) => (
              <Grid item xs={12} md={4} key={idx}>
                <ActionPriorityCard action={action} onViewDetails={setSelectedInsight} />
              </Grid>
            ))}
          </Grid>
        </Grid>
      </Grid>

      {/* Insight Detail Dialog */}
      <Dialog
        open={Boolean(selectedInsight)}
        onClose={() => setSelectedInsight(null)}
        maxWidth="md"
        fullWidth
      >
        {selectedInsight && (
          <>
            <DialogTitle>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h6">
                  {selectedInsight.title || selectedInsight.pattern || selectedInsight.initiative}
                </Typography>
                <IconButton onClick={() => setSelectedInsight(null)}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>

            <DialogContent dividers>
              {/* Common description or initiative text */}
              <Typography paragraph>
                {selectedInsight.description || selectedInsight.initiative}
              </Typography>

              {/* Key Findings */}
              {selectedInsight.key_findings && (
                <Box mt={3}>
                  <Typography variant="h6" gutterBottom>
                    Key Findings
                  </Typography>
                  <List>
                    {selectedInsight.key_findings.map((finding, idx) => (
                      <ListItem key={idx}>
                        <ListItemText primary={finding} />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}


              {/* Recommendation-specific content */}
              {selectedInsight.recommendation && (
                <Box mt={3}>
                  <Typography variant="h6" gutterBottom>
                    Recommendation
                  </Typography>
                  <Typography paragraph>{selectedInsight.recommendation}</Typography>
                </Box>
              )}


              {/* Action Priority content */}
              {selectedInsight.resources_required && (
                <Box mt={3}>
                  <Typography variant="h6" gutterBottom>
                    Required Resources
                  </Typography>
                  {selectedInsight.resources_required.map((resource, idx) => (
                    <Box key={idx} mb={2}>
                      <Typography variant="subtitle1">{resource.team}</Typography>
                      <Typography color="text.secondary" gutterBottom>
                        {resource.effort}
                      </Typography>
                      <List dense>
                        {resource.priority_tasks.map((task, taskIdx) => (
                          <ListItem key={taskIdx}>
                            <ListItemText primary={task} />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  ))}
                </Box>
              )}

              {/* Pattern-specific content */}
              {selectedInsight.confidence && (
                <Box mt={3}>
                  <Typography variant="h6" gutterBottom>
                    Pattern Details
                  </Typography>
                  <Typography variant="subtitle1" gutterBottom>
                    Confidence: {(selectedInsight.confidence * 100).toFixed(0)}%
                  </Typography>
                  {selectedInsight.growth_rate && (
                    <Typography color="error">
                      {selectedInsight.growth_rate}
                    </Typography>
                  )}
                </Box>
              )}
            </DialogContent>

            <DialogActions>
              <Button onClick={() => setSelectedInsight(null)}>Close</Button>
              <Button variant="contained" color="primary">
                Take Action
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default AIInsights;
